import React, { useState } from 'react';
import { type FrequentContact } from '../services/frequentContacts.service';
import {
  Edit,
  Trash2,
  MoreHorizontal,
  Search,
  Filter,
  User,
  Mail,
  Users
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { cn } from "@/lib/utils";
import { formatDate } from '@/lib/format';
import { useTranslation } from 'react-i18next';

const typeLabels = {
  supplier: 'Lieferant',
  warehouse: 'Lager',
  shipping: 'Versand',
  vendor: 'Anbieter',
  partner: 'Partner',
  other: 'Sonstiges'
};

const typeColors = {
  supplier: 'bg-blue-100 text-blue-700 border-blue-200',
  warehouse: 'bg-amber-100 text-amber-700 border-amber-200',
  shipping: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  vendor: 'bg-purple-100 text-purple-700 border-purple-200',
  partner: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  other: 'bg-slate-100 text-slate-700 border-slate-200'
};

interface ContactDataTableProps {
  contacts: FrequentContact[];
  isLoading: boolean;
  onEdit: (contact: FrequentContact) => void;
  onDelete: (contact: FrequentContact) => void;
  isDeletePending: boolean;
}

export const ContactDataTable = ({
  contacts,
  isLoading,
  onEdit,
  onDelete,
  isDeletePending
}: ContactDataTableProps) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = 
      contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = typeFilter === 'all' 
      ? true 
      : contact.type === typeFilter;

    return matchesSearch && matchesType;
  });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={t('common:x3.searchContacts', 'Kontakte suchen...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-9 text-sm border-slate-200 focus-visible:ring-slate-200"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[180px] h-9 border-slate-200 focus:ring-slate-200">
            <div className="flex items-center gap-2 text-slate-600">
              <Filter className="h-4 w-4" />
              <SelectValue placeholder={t('common:x3.filterType', 'Typ filtern')} />
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common:x3.allTypes', 'Alle Typen')}</SelectItem>
            <SelectItem value="supplier">{t('agent:dept.supplier', 'Lieferant')}</SelectItem>
            <SelectItem value="warehouse">{t('agent:dept.warehouse', 'Lager')}</SelectItem>
            <SelectItem value="shipping">{t('agent:dept.shipping', 'Versand')}</SelectItem>
            <SelectItem value="vendor">{t('agent:dept.vendor', 'Anbieter')}</SelectItem>
            <SelectItem value="partner">{t('agent:dept.partner', 'Partner')}</SelectItem>
            <SelectItem value="other">{t('agent:dept.other', 'Sonstiges')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow className="hover:bg-transparent border-slate-200">
              <TableHead className="w-[250px] font-semibold text-slate-900 h-11">{t('common:field.name', 'Name')}</TableHead>
              <TableHead className="w-[300px] font-semibold text-slate-900 h-11">{t('common:field.email', 'E-Mail')}</TableHead>
              <TableHead className="w-[150px] font-semibold text-slate-900 h-11">{t('common:field.category', 'Typ')}</TableHead>
              <TableHead className="w-[150px] font-semibold text-slate-900 h-11">{t('settings:tbl.createdAt', 'Erstellt am')}</TableHead>
              <TableHead className="w-[100px] text-right font-semibold text-slate-900 h-11">{t('common:field.actions', 'Aktionen')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="h-32 text-center text-slate-500">
                  {t('common:state.loading', 'Laden...')}
                </TableCell>
              </TableRow>
            ) : filteredContacts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-32 text-center">
                  <div className="flex flex-col items-center justify-center text-slate-500">
                    <Users className="h-8 w-8 mb-2 opacity-20" />
                    <p>{t('common:state.noContacts', 'Keine Kontakte gefunden')}</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filteredContacts.map((contact) => (
                <TableRow key={contact.id} className="hover:bg-slate-50/50 border-slate-100 group">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-medium text-xs">
                        {contact.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="font-medium text-slate-900">{contact.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 text-slate-600">
                      <Mail className="h-3.5 w-3.5 opacity-50" />
                      {contact.email}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant="secondary" 
                      className={cn(
                        "h-5 px-2 text-[10px] font-medium border",
                        typeColors[contact.type as keyof typeof typeColors] || typeColors.other
                      )}
                    >
                      {typeLabels[contact.type as keyof typeof typeLabels] || 'Sonstiges'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-slate-500 text-sm">
                    {formatDate(contact.created_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEdit(contact)}
                        className="h-8 w-8 text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onDelete(contact)}
                        className="h-8 w-8 text-slate-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
